/* ============================================================
   Glass Toast — shared toast system (replaces all Toastify usage; the
   Toastify CDN was removed 2026-07-20 — the sole remaining caller,
   theme.js's unreachable legacy decklist flow, would ReferenceError only
   if that dead UI were ever re-wired).
   Pure queue reducer is Node-testable:
   node --test test/toast.test.mjs
   Browser layer (Task 2) renders into #glass-toasts and two
   visually-hidden aria-live announcer regions.
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api; // Node (unit tests)
  } else {
    root.Toast = api; // Browser (container created lazily on first show)
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var MAX_VISIBLE = 3;

  var TONE_CLASSES = {
    info: "glass-toast--info",
    success: "glass-toast--success",
    error: "glass-toast--error"
  };

  function toneClass(tone) {
    return Object.prototype.hasOwnProperty.call(TONE_CLASSES, tone)
      ? TONE_CLASSES[tone]
      : TONE_CLASSES.info;
  }

  function enqueue(state, toast) {
    var toasts = state.toasts;
    for (var i = 0; i < toasts.length; i++) {
      if (toasts[i].message === toast.message && toasts[i].tone === toast.tone) {
        var copy = toasts.slice();
        copy[i] = {
          id: toasts[i].id,
          message: toasts[i].message,
          tone: toasts[i].tone,
          duration: toasts[i].duration,
          refreshCount: toasts[i].refreshCount + 1
        };
        return { toasts: copy };
      }
    }
    var next = toasts.concat([{
      id: toast.id,
      message: toast.message,
      tone: toast.tone,
      duration: toast.duration,
      refreshCount: 0
    }]);
    if (next.length > MAX_VISIBLE) next = next.slice(next.length - MAX_VISIBLE);
    return { toasts: next };
  }

  function expire(state, id) {
    return {
      toasts: state.toasts.filter(function (t) { return t.id !== id; })
    };
  }

  /* ---------- Browser-only below (no-ops in Node) ---------- */

  var state = { toasts: [] };
  var idCounter = 0;
  var timers = {};      // id -> setTimeout handle
  var expiresAt = {};   // id -> epoch ms when the active timer fires
  var remaining = {};   // id -> ms left while hover-paused
  var refreshSeen = {}; // id -> last refreshCount reflected in the DOM
  var container = null;
  var politeRegion = null;
  var assertiveRegion = null;

  function ensureContainer() {
    if (container) return;
    container = document.createElement("div");
    container.id = "glass-toasts";
    politeRegion = document.createElement("div");
    politeRegion.className = "glass-toast-announcer";
    politeRegion.setAttribute("aria-live", "polite");
    assertiveRegion = document.createElement("div");
    assertiveRegion.className = "glass-toast-announcer";
    assertiveRegion.setAttribute("aria-live", "assertive");
    document.body.appendChild(container);
    document.body.appendChild(politeRegion);
    document.body.appendChild(assertiveRegion);
  }

  function stopTimer(id) {
    if (timers[id]) {
      clearTimeout(timers[id]);
      delete timers[id];
    }
  }

  function startTimer(id, ms) {
    stopTimer(id);
    expiresAt[id] = Date.now() + ms;
    timers[id] = setTimeout(function () { dismiss(id); }, ms);
  }

  function forgetToast(id) {
    stopTimer(id);
    delete expiresAt[id];
    delete remaining[id];
    delete refreshSeen[id];
  }

  function dismiss(id) {
    state = expire(state, id);
    sync();
  }

  function announce(toast) {
    var region = toast.tone === "error" ? assertiveRegion : politeRegion;
    region.textContent = "";
    setTimeout(function () { region.textContent = toast.message; }, 30);
  }

  function makeEl(toast) {
    var el = document.createElement("div");
    el.className = "glass-toast " + toneClass(toast.tone);
    el.setAttribute("data-toast-id", String(toast.id));
    var msg = document.createElement("span");
    msg.className = "glass-toast__msg";
    // textContent, never innerHTML: no escaping burden, no XSS surface.
    msg.textContent = toast.message;
    el.appendChild(msg);
    el.addEventListener("mouseenter", function () {
      if (timers[toast.id]) {
        remaining[toast.id] = Math.max(500, expiresAt[toast.id] - Date.now());
        stopTimer(toast.id);
      }
    });
    el.addEventListener("mouseleave", function () {
      if (remaining[toast.id]) {
        startTimer(toast.id, remaining[toast.id]);
        delete remaining[toast.id];
      }
    });
    return el;
  }

  function removeEl(el) {
    var removed = false;
    function remove() {
      if (removed) return;
      removed = true;
      if (el.parentNode) el.parentNode.removeChild(el);
    }
    el.classList.remove("is-in");
    el.addEventListener("transitionend", remove);
    setTimeout(remove, 400); // fallback if transitions are disabled
  }

  function sync() {
    ensureContainer();
    var byId = {};
    state.toasts.forEach(function (t) { byId[t.id] = t; });
    Array.prototype.slice.call(container.children).forEach(function (el) {
      var id = parseInt(el.getAttribute("data-toast-id"), 10);
      if (!byId[id]) {
        forgetToast(id);
        removeEl(el);
      }
    });
    state.toasts.forEach(function (t) {
      var el = container.querySelector('[data-toast-id="' + t.id + '"]');
      if (!el) {
        el = makeEl(t);
        container.appendChild(el);
        void el.offsetWidth; // flush so the enter transition runs
        el.classList.add("is-in");
        refreshSeen[t.id] = 0;
        startTimer(t.id, t.duration);
        announce(t);
      } else if (t.refreshCount !== refreshSeen[t.id]) {
        // Duplicate-collapse: same element, timer resets from scratch. If the
        // toast is hover-paused, stay paused — just reset the stored
        // remainder so mouseleave restarts with the full duration.
        refreshSeen[t.id] = t.refreshCount;
        if (remaining[t.id]) {
          remaining[t.id] = t.duration;
        } else {
          startTimer(t.id, t.duration);
        }
        announce(t);
      }
    });
  }

  function show(message, opts) {
    if (typeof document === "undefined") return;
    opts = opts || {};
    var tone = Object.prototype.hasOwnProperty.call(TONE_CLASSES, opts.tone)
      ? opts.tone
      : "info";
    var duration = typeof opts.duration === "number" ? opts.duration : 2500;
    state = enqueue(state, {
      id: ++idCounter,
      message: String(message),
      tone: tone,
      duration: duration
    });
    sync();
  }

  function success(message) {
    show(message, { tone: "success" });
  }

  function error(message) {
    show(message, { tone: "error", duration: 4000 });
  }

  return {
    MAX_VISIBLE: MAX_VISIBLE,
    toneClass: toneClass,
    enqueue: enqueue,
    expire: expire,
    show: show,
    success: success,
    error: error
  };
});
